import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetPlotsList } from '@/modules/periodicTasks/apihooks';
import Field from '@/modules/periodicTasks/components/Create/Steps/CropsAndFields/Field';
import { PeriodicOperationCategory } from '@/modules/periodicTasks/components/Details/logic';
import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import { useGetPropertyCrops } from '@/modules/settings/property/apiHooks';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { setExactHour } from '@/shared/lib/dates';

import Frame from '~/images/shared/picture-frame.svg';
import Tree from '~/images/shared/tree.svg';

const CropsAndFields = () => {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const [selectedAsset] = useStore((store) => store.asset);
  const { data: crops, fetchNextPage } = useGetPropertyCrops({
    propertyId: selectedAsset?.id,
    pageNumber: 1,
    pageSize: 100,
  });
  const cropsList = crops?.pages?.flatMap((page) => page?.items);

  const { control, watch, trigger, resetField } =
    useFormContext<IPeriodicTasks.IOperationFormSchema>();
  const cropsIds = watch('crops');
  const rateWatcher = watch('products.0.rate');
  const isIrrigation =
    watch('operationType.id') === PeriodicOperationCategory.Irrigation;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'plots',
  });

  const { data: plotsList } = useGetPlotsList({
    entityId: cropsIds && !!cropsIds.length ? selectedAsset?.id : undefined,
    cropsIds: cropsIds && !!cropsIds.length ? cropsIds.join(',') : undefined,
  });

  const handleAppend = async () => {
    const isValid = await trigger('plots');

    if (isValid) {
      const newField = {
        plot: {
          plotId: null,
          plotName: null,
          plotArea: null,
          areaUnit: null,
          cropId: null,
          cropName: null,
          cropImage: null,
        },
        workingArea: null,
        startTime: setExactHour(6),
        endTime: setExactHour(7),
      };

      if (rateWatcher)
        openModal(
          <ConfirmationModal
            header={t('add-field-confirmation')}
            message={t(
              isIrrigation ? 'water-quantity-warning' : 'inputs-deleted-warning'
            )}
            variant='warning'
            confirmText={t('add-new-field')}
            onConfirm={() => {
              append(newField);
              resetField('products');
            }}
          />
        );
      else append(newField);
    }
  };

  const handleRemove = (index: number) => {
    if (rateWatcher)
      openModal(
        <ConfirmationModal
          header={t('delete-field-confirmation')}
          message={t(
            isIrrigation ? 'water-quantity-warning' : 'inputs-deleted-warning'
          )}
          variant='danger'
          confirmText={t('delete')}
          onConfirm={() => {
            remove(index);
            resetField('products');
          }}
        />
      );
    else remove(index);
  };

  const onEdit = (toggleEdit: () => void) => {
    if (rateWatcher)
      openModal(
        <ConfirmationModal
          header={t('edit-field-confirmation')}
          message={t(
            isIrrigation ? 'water-quantity-warning' : 'inputs-deleted-warning'
          )}
          variant='warning'
          confirmText={t('edit')}
          onConfirm={() => {
            toggleEdit();
            resetField('products');
          }}
        />
      );
    else toggleEdit();
  };

  return (
    <div className='flex flex-col gap-24'>
      <div className='border-b-xs border-b-neutralVariant-90 pb-24'>
        <div className='mb-24 flex items-center gap-8'>
          <Tree width='24' height='24' />
          <Typography weight='semiBold'>{t('crops')}</Typography>
        </div>
        <InfiniteSelectField
          isSearchable
          isMulti
          width='w-full min-w-0'
          borderColor={themeColors().grey[200]}
          borderRadius='1rem'
          id='crops'
          label={t('crops')}
          placeholder={t('crops')}
          list={cropsList ?? []}
          fetchNextPage={fetchNextPage}
          py='0.5rem'
          getLabel={(option: IPeriodicTasks.ICropSchema) => option.name}
          getValue={(option: IPeriodicTasks.ICropSchema) => option.id}
          formatOptionLabel={(option: IPeriodicTasks.ICropSchema) => (
            <div className='flex items-center gap-1'>
              {option.image && <CircularBgImage imageSrc={option.image} />}
              {option.name}
            </div>
          )}
        />
      </div>
      <div
        className={classNames(
          (!cropsIds || cropsIds.length === 0) &&
            'pointer-events-none opacity-5'
        )}
      >
        <div className='mb-24 flex items-center gap-8'>
          <Frame width='24' height='24' />
          <Typography weight='semiBold'>{t('fields')}</Typography>
        </div>
        {fields.map((field, index) => (
          <Field
            plotsList={plotsList ?? []}
            key={field.id}
            index={index}
            handleAppend={handleAppend}
            handleRemove={() => handleRemove(index)}
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  );
};

export default CropsAndFields;
